/**
 * Last-known route persistence for native cold launches.
 *
 * Capacitor Preferences (key/value, native-side, survives app kill) caches
 * the last in-app route the user navigated to. On cold launch — when the
 * native shell hits `https://app.alumnia.net/` — we read the cached route
 * and `router.replace` to it, so the user lands on the deep route they
 * were last on (`/in/acme/admin/members?tab=action-queue`), not the
 * generic dashboard root.
 *
 * Without this, cold launch always lands on the user's primary org root
 * via `<NativeRedirect>` — losing any deep context (admin queue tab,
 * specific member detail, ongoing onboarding step).
 *
 * Pattern matches the cold-launch deep-link UX in Linear, Notion, and
 * Stripe Dashboard, all of which restore the last-visited surface
 * pixel-perfect on relaunch.
 *
 * Storage:
 *   key   = "alumnia:last-route"
 *   value = the pathname + searchParams (e.g. "/in/acme/admin/members?tab=action-queue")
 *
 * Routes we never cache:
 *   - "/" (the bootstrap target — would create a redirect loop)
 *   - "/login" / "/signup" (transient pre-auth flows)
 *   - "/auth/*" (OAuth callbacks)
 *
 * The Preferences plugin is async; callers must await. Failures are
 * logged and never thrown — we always degrade to the existing
 * NativeRedirect path on read errors.
 */

/**
 * Cache key — versioned so route-shape changes (org slug rename, segment
 * removal, deep-link path rework) can invalidate the cached value cleanly
 * by bumping the suffix. On the next cold launch the read miss falls back
 * to the user's primary org dashboard root via NativeRedirect, then the
 * cache rebuilds itself as the user navigates.
 *
 * Bump policy:
 *   • Bump when a previously-valid path shape is removed or renamed.
 *   • Do NOT bump for additive changes (new routes, new query params).
 *   • Document the bump alongside the route change in CHANGELOG.
 */
const KEY = 'alumnia:last-route:v1'

/**
 * Routes that should never be cached (would loop or land on transient state).
 */
const NON_CACHEABLE_PREFIXES = ['/login', '/signup', '/auth/', '/']

function shouldCache(pathname: string): boolean {
  // "/" is exact, the rest are prefix matches.
  if (pathname === '/') return false
  return !NON_CACHEABLE_PREFIXES.slice(0, -1).some((prefix) => pathname.startsWith(prefix))
}

/**
 * Persist the current route to native storage. No-op on web. No-op for
 * non-cacheable routes (login/signup/auth/root). Errors are swallowed —
 * caching is a UX optimization, not a correctness requirement.
 */
export async function rememberRoute(pathname: string, search: string = ''): Promise<void> {
  if (!shouldCache(pathname)) return
  try {
    const { Preferences } = await import('@capacitor/preferences')
    await Preferences.set({ key: KEY, value: pathname + search })
  } catch {
    // Plugin not available (web build) or write failed — silently degrade.
  }
}

/**
 * Read the cached route. Returns null on web, when nothing cached, or on
 * any plugin error.
 */
export async function recallRoute(): Promise<string | null> {
  try {
    const { Preferences } = await import('@capacitor/preferences')
    const { value } = await Preferences.get({ key: KEY })
    return value && value.length > 0 ? value : null
  } catch {
    return null
  }
}

/**
 * Clear the cached route. Call on sign-out so the next cold-launch as a
 * different user (or after auth expiry) doesn't restore the previous
 * user's deep route.
 */
export async function forgetRoute(): Promise<void> {
  try {
    const { Preferences } = await import('@capacitor/preferences')
    await Preferences.remove({ key: KEY })
  } catch {
    // Plugin not available — ignore.
  }
}
